<script setup>
import { Power0, gsap } from 'gsap'

const props = defineProps({ cardItem: Array, cardTitle: String, cardSubtitle: Object })

const { width } = useWindowSize()
const number1 = ref(0)
const number2 = ref(0)
const number3 = ref(0)
const tweened = reactive({
  number1: 0,
  number2: 0,
  number3: 0,
})
const target = ref(null)
const { elementX, elementY, isOutside, elementHeight, elementWidth } = useMouseInElement(target)

const cardContent = computed(() => renderRichText(props.cardSubtitle))

// `width` (SSR fallback: Infinity) and the mouse-tracking refs from useMouseInElement all read real
// browser state that isn't available during SSR — evaluating them on the very first client render
// (before mount) produces a different `transform` value than what the server rendered, which Vue
// flags as a hydration mismatch. This is a purely client-side hover effect anyway, so gate it behind
// `isMounted` and let both SSR and the pre-mount client render agree on an empty transform.
const isMounted = ref(false)

const cardTransform = computed(() => {
  if (isMounted.value && width.value >= 768) {
    const MAX_ROTAION = 8
    const rX = (
      MAX_ROTAION / 2
      - (elementY.value / elementHeight.value) * MAX_ROTAION
    ).toFixed(2)
    const rY = (
      (elementX.value / elementWidth.value) * MAX_ROTAION - MAX_ROTAION / 2
    ).toFixed(2)
    return isOutside.value ? '' : `perspective(${elementWidth.value}px) rotateX(${rX}deg) rotateY(${rY}deg)`
  }
  return ''
})

watch([number1, number2, number3], ([n1, n2, n3]) => {
  gsap.to(tweened, {
    duration: 2,
    number1: Number(n1) || 0,
    number2: Number(n2) || 0,
    number3: Number(n3) || 0,
    ease: Power0,
  })
})

onMounted(() => {
  isMounted.value = true
  number1.value = props.cardItem[0].title.replace(',', '')
  number2.value = props.cardItem[1].title.replace('%', '')
  number3.value = props.cardItem[2].title.replace(',', '')
})
</script>

<template>
  <div
    ref="target"
    :style="{
      perspective: '1000px',
      transform: cardTransform,
    }" class="backface-hidden relative z-20 mr-auto mt-custom-lg transition-transform duration-50 ease-in-out"
  >
    <div class="relative z-2 rounded-md p-4 shadow-lg">
      <h3 class="mb-1 text-left text-lg text-white font-bold">
        {{ cardTitle }}
      </h3>
      <div
        class="hero-card relative mb-3 flex flex-col gap-x-4 gap-y-6 text-left sm:grid-cols-3 sm:flex-row md:gap-x-8"
      >
        <div v-for="(content, index) in cardItem" :key="content._uid" class="hero-card-item relative">
          <div class="text-3xl text-white font-bold">
            <span v-if="index === 0 && tweened.number1 !== 0">{{ tweened.number1.toFixed(0) }}
            </span>
            <span v-else-if="index === 1 && tweened.number2 !== 0">
              {{ tweened.number2.toFixed(0) }}%
            </span>
            <span v-else-if="index === 2 && tweened.number3 !== 0">
              {{ tweened.number3.toFixed(0) }}
            </span>
            <span v-else>?</span>
          </div>
          <p class="max-w-[150px] text-xs text-white/90 font-medium leading-[1.5] md:text-sm">
            {{ content.subtitle }}
          </p>
        </div>
      </div>
      <div class="hero-card-subtitle" v-html="cardContent" />
    </div>
    <div class="grainy z-1 rounded-md" />
    <div class="absolute inset-0 z-0 h-full w-full border border-white/10 rounded-md backdrop-blur-8" />
  </div>
</template>

<style>
.hero-container .hero-card .hero-card-item:nth-child(2) {
  @apply sm:py-0 sm:px-4 md:px-8 sm:before:absolute before:content-empty before:w-[1px] before:h-full before:bg-white/50 before:left-0 before:inset-y-0
}
.hero-container .hero-card .hero-card-item:nth-child(2) {
  @apply sm:after:absolute after:content-empty after:w-[1px] after:h-full after:bg-white/50 after:right-0 after:inset-y-0
}

.hero-card-subtitle p b {
  @apply text-sm font-semibold text-white/90;
}

.hero-card-subtitle p {
  @apply text-sm leading-[1.6] text-white/90 [&:not(:last-child)]:mb-1.5;
}
</style>
